import api from './api'

export type MentorshipSkill = {
  id: string
  skillId?: string
  mentorshipSkillId?: string | null
  coreSkillId?: string | null
  tenantId: string
  name: string
  category?: string | null
  description?: string | null
  status: string
  source?: 'shared_catalog' | 'worker_profile' | 'mentorship_catalog'
  workerHasSkill?: boolean
  mentorClaimStatus?: 'none' | 'pending' | 'approved' | 'rejected' | 'suspended'
  mentorApproved?: boolean
  canCreateMentorClaim?: boolean
  canCreateMentorRate?: boolean
  canCreateLearningGoal?: boolean
  learningGoalStatus?: 'none' | 'active' | 'matched' | 'completed' | 'cancelled'
  acquiredThroughMentorship?: boolean
  createdAt: string
  updatedAt: string
}

export type MentorshipWorkerPreferences = {
  id: string
  tenantId: string
  workerProfileId: string
  mentorModeEnabled: boolean
  apprenticeModeEnabled: boolean
  remoteMentorshipEnabled: boolean
  onsiteMentorshipEnabled: boolean
  bio?: string | null
}

export type MentorshipSkillClaim = {
  id: string
  workerProfileId: string
  workerName?: string
  skillId: string
  skillName?: string
  status: string
  evidenceDocumentId?: string | null
  notes?: string | null
}

export type MentorshipLearningGoal = {
  id: string
  workerProfileId: string
  workerName?: string
  skillId: string
  skillName?: string
  status: string
  priority?: string | null
  notes?: string | null
  suggestedMentorCount?: number
}

export type MentorshipMatch = Record<string, any>
export type MentorshipSession = Record<string, any>
export type MentorshipReview = Record<string, any>
export type MentorshipMentorRate = Record<string, any>
export type MentorshipBookingRequest = Record<string, any>
export type MentorshipPlatformFeeSetting = Record<string, any>
export type MentorshipPaymentRecord = Record<string, any>
export type MentorshipParticipant = Record<string, any>

export const mentorshipService = {
  listParticipants: () => api.get('/api/admin/mentorship/participants'),
  listSkills: () => api.get('/api/admin/mentorship/skills'),
  createSkill: (payload: Partial<MentorshipSkill>) => api.post('/api/admin/mentorship/skills', payload),
  updateSkill: (skillId: string, payload: Partial<MentorshipSkill>) => api.patch(`/api/admin/mentorship/skills/${skillId}`, payload),

  listMentorClaims: () => api.get('/api/admin/mentorship/mentor-claims'),
  approveClaim: (claimId: string, notes?: string) => api.post(`/api/admin/mentorship/mentor-claims/${claimId}/approve`, { notes }),
  rejectClaim: (claimId: string, notes?: string) => api.post(`/api/admin/mentorship/mentor-claims/${claimId}/reject`, { notes }),
  suspendClaim: (claimId: string, notes?: string) => api.post(`/api/admin/mentorship/mentor-claims/${claimId}/suspend`, { notes }),

  listLearningGoals: () => api.get('/api/admin/mentorship/learning-goals'),
  listMatches: () => api.get('/api/admin/mentorship/matches'),
  createMatch: (payload: any) => api.post('/api/admin/mentorship/matches', payload),
  updateMatch: (matchId: string, payload: any) => api.patch(`/api/admin/mentorship/matches/${matchId}`, payload),
  listSessions: () => api.get('/api/admin/mentorship/sessions'),
  createSession: (payload: any) => api.post('/api/admin/mentorship/sessions', payload),
  updateSession: (sessionId: string, payload: any) => api.patch(`/api/admin/mentorship/sessions/${sessionId}`, payload),
  completeSession: (sessionId: string, payload: any = {}) => api.post(`/api/admin/mentorship/sessions/${sessionId}/complete`, payload),
  cancelSession: (sessionId: string, notes?: string) => api.post(`/api/admin/mentorship/sessions/${sessionId}/cancel`, { notes }),
  listRates: () => api.get('/api/admin/mentorship/rates'),
  listBookings: () => api.get('/api/admin/mentorship/bookings'),
  listPaymentRecords: () => api.get('/api/admin/mentorship/payment-records'),
  updatePaymentRecord: (paymentRecordId: string, payload: any) => api.patch(`/api/admin/mentorship/payment-records/${paymentRecordId}`, payload),
  listPlatformFees: () => api.get('/api/admin/mentorship/platform-fees'),
  createPlatformFee: (payload: any) => api.post('/api/admin/mentorship/platform-fees', payload),
  updatePlatformFee: (feeSettingId: string, payload: any) => api.patch(`/api/admin/mentorship/platform-fees/${feeSettingId}`, payload),

  getMyProfile: () => api.get('/api/workers/me/mentorship'),
  updateMyPreferences: (payload: Partial<MentorshipWorkerPreferences>) => api.patch('/api/workers/me/mentorship/preferences', payload),
  listMySkills: () => api.get('/api/workers/me/mentorship/skills'),
  createMyClaim: (payload: any) => api.post('/api/workers/me/mentorship/mentor-claims', payload),
  listMyClaims: () => api.get('/api/workers/me/mentorship/mentor-claims'),
  createMyLearningGoal: (payload: any) => api.post('/api/workers/me/mentorship/learning-goals', payload),
  listMyLearningGoals: () => api.get('/api/workers/me/mentorship/learning-goals'),
  listSuggestedMentors: (skillId: string, paidOnly = false) => api.get('/api/workers/me/mentorship/suggested-mentors', { params: { skillId, paidOnly } }),
  listMyMatches: () => api.get('/api/workers/me/mentorship/matches'),
  listMySessions: () => api.get('/api/workers/me/mentorship/sessions'),
  createMyReview: (payload: any) => api.post('/api/workers/me/mentorship/reviews', payload),
  listMyRates: () => api.get('/api/workers/me/mentorship/rates'),
  createMyRate: (payload: any) => api.post('/api/workers/me/mentorship/rates', payload),
  updateMyRate: (rateId: string, payload: any) => api.patch(`/api/workers/me/mentorship/rates/${rateId}`, payload),
  listMyBookings: () => api.get('/api/workers/me/mentorship/bookings'),
  createMyBooking: (payload: any) => api.post('/api/workers/me/mentorship/bookings', payload),
  acceptMyBooking: (bookingId: string, notes?: string) => api.post(`/api/workers/me/mentorship/bookings/${bookingId}/accept`, { notes }),
  rejectMyBooking: (bookingId: string, notes?: string) => api.post(`/api/workers/me/mentorship/bookings/${bookingId}/reject`, { notes }),
  cancelMyBooking: (bookingId: string, notes?: string) => api.post(`/api/workers/me/mentorship/bookings/${bookingId}/cancel`, { notes }),
  completeMyBooking: (bookingId: string, notes?: string) => api.post(`/api/workers/me/mentorship/bookings/${bookingId}/complete`, { notes }),
  listMyPaymentRecords: () => api.get('/api/workers/me/mentorship/payment-records'),

  getMyMentorshipProfile: () => api.get('/api/workers/me/mentorship'),
  updateMyMentorshipPreferences: (payload: Partial<MentorshipWorkerPreferences>) => api.patch('/api/workers/me/mentorship/preferences', payload),
  listMentorshipSkills: () => api.get('/api/workers/me/mentorship/skills'),
  createMentorClaim: (payload: any) => api.post('/api/workers/me/mentorship/mentor-claims', payload),
  listMyMentorClaims: () => api.get('/api/workers/me/mentorship/mentor-claims'),
  createLearningGoal: (payload: any) => api.post('/api/workers/me/mentorship/learning-goals', payload),
  updateMyMatchStatus: (matchId: string, payload: any) => api.patch(`/api/workers/me/mentorship/matches/${matchId}`, payload),
  createReview: (payload: any) => api.post('/api/workers/me/mentorship/reviews', payload),
  createBooking: (payload: any) => api.post('/api/workers/me/mentorship/bookings', payload),
  acceptBooking: (bookingId: string, notes?: string) => api.post(`/api/workers/me/mentorship/bookings/${bookingId}/accept`, { notes }),
  rejectBooking: (bookingId: string, notes?: string) => api.post(`/api/workers/me/mentorship/bookings/${bookingId}/reject`, { notes }),
  cancelBooking: (bookingId: string, notes?: string) => api.post(`/api/workers/me/mentorship/bookings/${bookingId}/cancel`, { notes }),
  completeBooking: (bookingId: string, notes?: string) => api.post(`/api/workers/me/mentorship/bookings/${bookingId}/complete`, { notes }),
}
